/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { useEffect, useRef, useState } from "react";

import { css, cx } from "@panda/css";

import { LogoList } from "./LogoList.molecule";
import { LogoListSectionProps } from "@components/elements/ref-sections";

const defaultLogoHeight = 54;
const gridLogoHeight = 90;
// Seconds to scroll one list's width.
const duration = 120;

const noTrackStyles = css({ display: "flex", justifyContent: "center", w: "full" });
const trackStyles = css({
	display: "flex",
	width: "max-content",
	// With reduced motion the track stands still on its first copy.
	"@media (prefers-reduced-motion: no-preference)": {
		animationName: "marqueeTrack",
		animationTimingFunction: "linear",
		animationIterationCount: "infinite",
	},
});

/**
 * Renders a list of logos either with an animation or in a grid.
 *
 * **Marquee structure:** the track holds two equal halves, each made of one
 * or more copies of the logo list placed side-by-side via flex (each list's
 * 32px inline padding adds up to the 64px inter-logo gap between copies).
 * The track animates `translateX(0)` → `translateX(-50%)` over the cycle
 * duration, which equals exactly one half's worth of scroll. When the
 * animation loops, the second half is in the position the first just
 * vacated — seamless infinite scroll, no visual jump, no overlap.
 *
 * A half repeats the list until it is at least as wide as the marquee, so a
 * short list still reads as an unbroken line instead of running out partway
 * across the screen.
 *
 * **Why not the older "two absolute lists with delay" pattern:** that
 * approach renders both copies at the same origin (position: absolute)
 * with a 50%-of-duration animation delay between them. Sounds equivalent
 * but isn't — both copies are absolutely positioned at the SAME (0,0) in
 * their parent and animated through a 200% range (translateX(100%) →
 * translateX(-100%)). At many points in the cycle, both are partially
 * visible at OVERLAPPING world positions, producing a visible double-up
 * (e.g. on a 17-logo EAC marquee you'd see Apple Arcade and PlayStation 4
 * stacked in the same horizontal slot).
 */
export const LogoMarquee = ({
	logos = [],
	disableMarquee = false,
	className,
}: LogoListSectionProps): React.JSX.Element | null => {
	const marquee = !disableMarquee;
	const hasLogos = !!logos?.length;

	// Copies of the list in each half of the track.
	const [repeat, setRepeat] = useState(1);
	const containerRef = useRef<HTMLDivElement>(null);
	const listRef = useRef<HTMLDivElement>(null);

	// A half must cover the marquee, or its end scrolls into view as a gap.
	// The list's width doesn't depend on the copy count, so this settles in one
	// pass; it re-runs when images load or the marquee resizes.
	useEffect(() => {
		const container = containerRef.current;
		const list = listRef.current;
		if (!marquee || !hasLogos || !container || !list) return;

		const observer = new ResizeObserver(() => {
			const listWidth = list.offsetWidth;
			if (listWidth) setRepeat(Math.max(1, Math.ceil(container.clientWidth / listWidth)));
		});
		observer.observe(container);
		observer.observe(list);
		return () => observer.disconnect();
	}, [marquee, hasLogos]);

	if (!logos) {
		return null;
	}

	// Panda can't statically extract `${height}px` from this ternary, so no
	// `h_90px` rule is generated and grid logos render at their natural size.
	// Swapping in a constant changes how every disableMarquee grid looks; do
	// that in its own change.
	const height = disableMarquee ? gridLogoHeight : defaultLogoHeight;

	if (!marquee) {
		return (
			<div className={cx("logo-marquee", css({ height: `${height}px`, w: "full" }), className)}>
				<div className={noTrackStyles}>
					<LogoList logos={logos} height={height} marquee={false} gridLayout={disableMarquee} />
				</div>
			</div>
		);
	}

	// We rely on `width: max-content` to size the flex track to EXACTLY its
	// copies' content width, so `translateX(-50%)` is exactly one half — a
	// seamless loop regardless of viewport. The duration scales with the copy
	// count to keep the scroll speed the same.
	//
	// Do NOT add a `minWidth` to the track. Anything wider than its content
	// makes `translateX(-50%)` translate further than one half — a visible
	// "double speed" stretch. `max-content` alone keeps box and content in
	// lockstep.
	return (
		<div
			ref={containerRef}
			className={cx("logo-marquee", css({ height: `${height}px`, overflow: "hidden", w: "full" }), className)}
		>
			<div className={cx("logo-marquee-track", trackStyles)} style={{ animationDuration: `${duration * repeat}s` }}>
				{Array.from({ length: repeat * 2 }, (_, i) => (
					<LogoList
						key={i}
						ref={i === 0 ? listRef : undefined}
						logos={logos}
						height={defaultLogoHeight}
						marquee
						aria-hidden={i > 0 || undefined}
						// Not `inert`: that would block clicks on the copies, which are
						// most of what is on screen.
						focusable={i === 0}
					/>
				))}
			</div>
		</div>
	);
};
